import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import ShinyText from "@/components/reactbits/ShinyText";
import ShinyLoader, { LOADER_COUNT } from "@/components/ShinyLoader";
import { EASE_OUT } from "@/lib/motion";

// ShinyText drops these straight into a CSS gradient, so the variables resolve
// on their own and the sweep re-points when the theme flips.
const NARRATION_BASE = "rgb(var(--c-dim))";
const NARRATION_SHINE = "rgb(var(--c-ink))";

// What the model is actually doing, in the order it does it, with the timings
// a typical analysis takes. These are a narration of the pipeline, not a
// progress report from it — the API returns once, at the end, and inventing a
// percentage would be a lie. Naming the work is the honest version of the same
// reassurance, and it's what makes ten seconds feel accounted for.
const PHASES = [
  { at: 0, text: "Fetching the article" },
  { at: 1900, text: "Reading it end to end" },
  { at: 3900, text: "Weighing the word choice" },
  { at: 5700, text: "Noting who gets quoted" },
  { at: 7400, text: "Looking for what's left out" },
  { at: 9100, text: "Scoring the slant" },
  { at: 10800, text: "Tidying up the verdict" },
];

// Compare runs two analyses and one more model call to weigh them against each
// other, so the singular narration was describing a third of the work and
// naming the wrong number of articles throughout. Same beats, same honesty
// about what is happening — the timings stretch because there is genuinely more
// of it, and the last beat is the comparison itself, which has no counterpart
// in a single scan.
const PHASES_PAIR = [
  { at: 0, text: "Fetching both articles" },
  { at: 2600, text: "Reading them end to end" },
  { at: 5400, text: "Weighing the word choice in each" },
  { at: 8200, text: "Noting who each one quotes" },
  { at: 11000, text: "Looking for what each leaves out" },
  { at: 13800, text: "Scoring both" },
  { at: 16600, text: "Setting the two framings against each other" },
];

// Past the last phase we genuinely don't know how much longer it will be, so
// stop implying it's nearly over. Sitting on "tidying up" for another thirty
// seconds is the one thing this narration could get actively wrong.
const OVERTIME_AT = 14000;
// Scaled to the pair narration, which is still naming real work at 16.6s.
const OVERTIME_PAIR_AT = 21000;
const OVERTIME_TEXT = "Still reading — this one runs long";

// Which loader the next analysis gets.
//
// Module scope because this component unmounts the moment a scan finishes;
// anything held in state resets with it and every run would open on the same
// loader. Advancing rather than drawing at random is what makes "different from
// the one you just saw" a guarantee instead of a seven-in-eight chance.
let loaderCursor = -1;

function takeNextLoader() {
  loaderCursor = (loaderCursor + 1) % LOADER_COUNT;
  return loaderCursor;
}

function useAnalysisNarration(phases, overtimeAt) {
  const [index, setIndex] = useState(0);
  const [overtime, setOvertime] = useState(false);

  // Claimed once, when the run starts, and held for the whole of it. A lazy ref
  // rather than a useState initialiser: StrictMode invokes an initialiser twice
  // in development, and this one moves a module-level cursor.
  const loaderRef = useRef(null);
  if (loaderRef.current === null) loaderRef.current = takeNextLoader();

  useEffect(() => {
    const timers = phases.slice(1).map((phase, i) =>
      setTimeout(() => setIndex(i + 1), phase.at));
    timers.push(setTimeout(() => setOvertime(true), overtimeAt));
    return () => timers.forEach(clearTimeout);
  }, [phases, overtimeAt]);

  return {
    index,
    overtime,
    text: overtime ? OVERTIME_TEXT : phases[index].text,
    // One loader for the whole run. The words still change on every beat; the
    // animation underneath them no longer does.
    step: loaderRef.current,
    // Held just short of full while overrunning: the bar should never claim to
    // be finished before the result actually arrives.
    progress: overtime ? 0.94 : (index + 1) / (phases.length + 1),
  };
}

function Narration({ text, reduceMotion, className }) {
  return (
    // The live region is this stable wrapper, not the keyed child. A keyed
    // child is a new element every phase, and swapping the element out from
    // under aria-live is how a live region silently stops announcing.
    <div role="status" aria-live="polite" className={className}>
      <motion.div
        key={text}
        // Reduced motion drops the travel and keeps the crossfade. The fade is
        // what makes one phase legibly replace the last; the six pixels of rise
        // are decoration, and decoration is what the preference is asking about.
        initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: EASE_OUT }}
      >
        <ShinyText
          text={text}
          speed={2.4}
          color={NARRATION_BASE}
          shineColor={NARRATION_SHINE}
          spread={100}
          disabled={reduceMotion}
        />
      </motion.div>
    </div>
  );
}

/**
 * The wait while an article is analyzed.
 *
 * `compact` fits it inside the peek dialog, where the full treatment would push
 * the article itself out of view. `pair` narrates a compare run, which is two
 * analyses and a verdict rather than one analysis.
 */
export default function LoadingStepper({ compact = false, pair = false }) {
  const { text, progress, overtime, step } = useAnalysisNarration(
    pair ? PHASES_PAIR : PHASES,
    pair ? OVERTIME_PAIR_AT : OVERTIME_AT,
  );
  const reduceMotion = useReducedMotion();

  return (
    <div
      className={compact ? "flex flex-col items-center py-5" : "mx-auto max-w-md py-8"}
      data-testid="loading-stepper"
    >
      <div className="flex flex-col items-center">
        <ShinyLoader index={step} compact={compact} />
        {!compact && (
          <p className="mt-4 font-serif text-lg font-bold text-ink">Analyzing…</p>
        )}
        <Narration
          text={text}
          reduceMotion={reduceMotion}
          className={`text-center font-medium ${compact ? "mt-4 h-5 text-sm" : "mt-2 h-6 text-[15px]"}`}
        />
      </div>

      {/* One bar rather than a checklist: with seven beats a list would be
          taller than the dialog it sits in. */}
      <div className={`${compact ? "mt-4 w-40" : "mt-6 w-full"} h-1 overflow-hidden rounded-full bg-subtle`}>
        {/* scaleX rather than width: this bar is on screen precisely while the
            main thread is busiest, which is the worst moment to be re-running
            layout every frame. The track already clips to a pill, so the fill
            needs no radius of its own. */}
        <motion.div
          className="h-full w-full origin-left bg-ink"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: progress, opacity: overtime ? [1, 0.55, 1] : 1 }}
          transition={{
            scaleX: { duration: 0.5, ease: EASE_OUT },
            opacity: overtime ? { duration: 1.6, repeat: Infinity } : { duration: 0 },
          }}
        />
      </div>
    </div>
  );
}
